import React, { useState } from 'react';
import { formatTemp } from '../../utils/weatherUtils';
import Icon from '../../utils/icons';
import './Sidebar.css';

function Sidebar({
  isOpen,
  locations,
  activeLocation,
  weatherData,
  onSelectLocation,
  onAddLocation,
  onRemoveLocation,
  onToggle,
  loading
}) {
  const [newLocation, setNewLocation] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addError, setAddError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!newLocation.trim() || isAdding) return;

    setIsAdding(true);
    setAddError(null);

    const result = await onAddLocation(newLocation.trim());
    
    if (result.success) {
      setNewLocation('');
      setShowAddForm(false);
    } else {
      setAddError(result.error || 'Failed to add location');
    }
    
    setIsAdding(false);
  };

  const handleRemove = (e, location) => {
    e.stopPropagation();
    const result = onRemoveLocation(location);
    if (!result.success) {
      alert(result.error);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setNewLocation('');
    setAddError(null);
  };

  return (
    <aside className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-header">
        <div className="logo">
          <span className="logo-icon">
            <Icon name="building" size={24} />
          </span>
          {isOpen && <span className="logo-text">DC Weather</span>}
        </div>
        <button className="toggle-btn" onClick={onToggle}>
          <Icon name={isOpen ? 'chevronLeft' : 'chevronRight'} size={14} />
        </button>
      </div>

      <nav className="sidebar-nav">
        <div className="nav-section">
          {isOpen && (
            <div className="nav-header">
              <h3 className="nav-title">Locations</h3>
              <span className="location-count">{locations.length}</span>
            </div>
          )}
          
          <ul className="location-list">
            {locations.map((location, index) => {
              const data = weatherData[location]?.current;
              const isActive = location === activeLocation;

              return (
                <li
                  key={location}
                  className={`location-item ${isActive ? 'active' : ''}`}
                  onClick={() => onSelectLocation(location)}
                  title={location}
                >
                  <div className="location-main">
                    <span className="location-icon">
                      {data?.condition?.icon ? (
                        <img
                          src={`https://openweathermap.org/img/wn/${data.condition.icon}.png`}
                          alt=""
                          className="weather-icon-small"
                        />
                      ) : (
                        <Icon name="mapPin" size={20} />
                      )}
                    </span>
                    {isOpen && (
                      <div className="location-info">
                        <span className="location-name">
                          {location.split(',')[0]}
                        </span>
                        <span className="location-region">
                          {location.split(',').slice(1).join(',').trim()}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {isOpen && data && (
                    <div className="location-weather">
                      <span className="location-temp">
                        {formatTemp(data.current?.temperature)}
                      </span>
                    </div>
                  )}

                  {isOpen && locations.length > 1 && (
                    <button
                      className="remove-btn"
                      onClick={(e) => handleRemove(e, location)}
                      title="Remove location"
                    >
                      <Icon name="x" size={12} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {isOpen && (
          <div className="add-location">
            {showAddForm ? (
              <form onSubmit={handleAddLocation} className="add-form">
                <div className="add-input-wrapper">
                  <Icon name="mapPin" size={16} className="input-icon" />
                  <input
                    type="text"
                    value={newLocation}
                    onChange={(e) => {
                      setNewLocation(e.target.value);
                      setAddError(null);
                    }}
                    placeholder="City, State, Country"
                    className={`add-input ${addError ? 'error' : ''}`}
                    autoFocus
                    disabled={isAdding}
                  />
                </div>
                
                {addError && (
                  <div className="add-error">
                    <Icon name="alertTriangle" size={12} />
                    <span>{addError}</span>
                  </div>
                )}

                <div className="add-hint">
                  Example: Dallas, TX, US or London, UK
                </div>

                <div className="add-actions">
                  <button 
                    type="submit" 
                    className="add-submit"
                    disabled={isAdding || !newLocation.trim()}
                  >
                    {isAdding ? (
                      <>
                        <span className="btn-spinner"></span>
                        Adding...
                      </>
                    ) : (
                      <>
                        <Icon name="plus" size={14} />
                        Add
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="add-cancel"
                    onClick={handleCancel}
                    disabled={isAdding}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                className="add-btn"
                onClick={() => setShowAddForm(true)}
              >
                <Icon name="plus" size={16} />
                <span>Add Location</span>
              </button>
            )}
          </div>
        )}
      </nav>

      {isOpen && (
        <div className="sidebar-footer">
          <div className="footer-item">
            <Icon name="refresh" size={14} className="footer-icon" />
            <span className="footer-label">Auto-refresh</span>
            <span className="footer-value">5 min</span>
          </div>
          <div className="footer-item">
            <Icon name="cloud" size={14} className="footer-icon" />
            <span className="footer-label">Locations saved</span>
            <span className="footer-value">{locations.length}</span>
          </div>
        </div>
      )}
    </aside>
  );
}

export default Sidebar;